// Copyright (c) 2026 Michael F. Kamprath
//
// entrance.js -- entrance animation engine for Ewe Can Count.
//
// All animation timing and appearance is driven by data in
// `public/animations.json` -- this file contains no hardcoded tuning
// constants.  Edit the JSON (or the values it's generated from) to
// tweak behavior.
//
// Architecture
// ------------
//
// An "entrance animation" for a theme has:
//
//   type          -- "fenceJump" (sheep) or "coopRamp" (chicks, cows)
//   standard/wide -- per-variant layout (points, velocities, delays)
//   tuning        -- visual knobs: arc height, step stride, etc.
//   cycles        -- named keyframe tables, see below
//   music         -- entrance-loop parameters (volume, fades)
//   nativeFacing  -- "left" | "right": direction the sprite image is drawn
//
// Keyframe cycles
// ---------------
//
// A cycle is a list of keyframes of the shape:
//
//   { t, image?, deltaX?, deltaY?, lastFrameDuration?, soundFile? }
//
// deltaX/Y are per-variant design-pixel offsets from the cycle's starting
// position.  imageFile swaps the sprite texture at that keyframe's time.
// Cycles are stretched to fit the duration of each motion "step" (a
// ramp-rung-to-ramp-rung jump, a barn-phase stride, etc.) -- matching
// the legacy CoreAnimation `setAnimationCycleTime` behavior.
//
// When a sprite is flipped (facing opposite of nativeFacing), deltaX is
// mirrored so the body-bob lunges in the correct direction.

import { Assets } from 'pixi.js';
import { playLoop, play as playOneShot, preload as preloadAudio } from '../audio/engine.js';

// --- Clock --------------------------------------------------------------

let _now = () => performance.now();
export function __setClockForTests(fn) { _now = fn; }
export function __resetClockForTests() { _now = () => performance.now(); }

// --- animations.json loading --------------------------------------------

let animationsCache = null;

export async function loadAnimationsData() {
  if (animationsCache) return animationsCache;
  const resp = await fetch('/animations.json');
  if (!resp.ok) throw new Error(`Failed to load animations.json: ${resp.status}`);
  animationsCache = await resp.json();
  return animationsCache;
}
export function __resetAnimationsCache() { animationsCache = null; }

// --- Tween primitives ---------------------------------------------------

function lerp(a, b, t) { return a + (b - a) * t; }
function easeInOut(t) { return -0.5 * (Math.cos(Math.PI * t) - 1); }
function easeOut(t) { return 1 - (1 - t) * (1 - t); }

export function animateOver(durationMs, onFrame, cancel) {
  return new Promise((resolve) => {
    const start = _now();
    let rafId = null;
    let settled = false;
    let unsubscribe = null;
    const finish = () => {
      if (settled) return;
      settled = true;
      if (unsubscribe) unsubscribe();
      resolve();
    };
    const stopRaf = () => {
      if (rafId !== null && typeof cancelAnimationFrame === 'function') {
        cancelAnimationFrame(rafId);
      }
      rafId = null;
    };
    const tick = () => {
      if (settled) return;
      if (cancel && cancel.cancelled) {
        stopRaf();
        finish();
        return;
      }
      if (cancel && cancel.fastForwarded) {
        stopRaf();
        try { onFrame(1, durationMs); } catch { /* swallow per-frame errors */ }
        finish();
        return;
      }
      const elapsed = _now() - start;
      const t = Math.min(1, durationMs > 0 ? elapsed / durationMs : 1);
      try { onFrame(t, elapsed); } catch { /* swallow per-frame errors */ }
      if (t >= 1) { finish(); return; }
      if (typeof requestAnimationFrame === 'function') {
        rafId = requestAnimationFrame(tick);
      } else {
        setTimeout(tick, 16);
      }
    };
    if (cancel && typeof cancel.onChange === 'function') {
      unsubscribe = cancel.onChange(() => {
        if (settled) return;
        if (cancel.cancelled) {
          stopRaf();
          finish();
          return;
        }
        if (cancel.fastForwarded) {
          stopRaf();
          try { onFrame(1, durationMs); } catch { /* swallow per-frame errors */ }
          finish();
        }
      });
    }
    tick();
  });
}

function waitForDelay(delayMs, signal) {
  return new Promise((resolve) => {
    if (delayMs <= 0 || signal?.cancelled || signal?.fastForwarded) {
      resolve();
      return;
    }
    let timerId = null;
    let settled = false;
    let unsubscribe = null;
    const finish = () => {
      if (settled) return;
      settled = true;
      if (timerId !== null) {
        try { clearTimeout(timerId); } catch { /* no-op */ }
      }
      if (unsubscribe) unsubscribe();
      resolve();
    };
    if (typeof signal?.onChange === 'function') {
      unsubscribe = signal.onChange(() => {
        if (signal.cancelled || signal.fastForwarded) finish();
      });
    }
    timerId = setTimeout(finish, delayMs);
  });
}

function restoreSpriteAtRest(sprite) {
  if (!sprite) return;
  const inner = getInner(sprite);
  sprite.visible = true;
  if (typeof sprite._finalX === 'number') sprite.x = sprite._finalX;
  if (typeof sprite._finalY === 'number') sprite.y = sprite._finalY;
  if (sprite._staticTexture) setSpriteTexture(sprite, sprite._staticTexture);
  setSpriteRotation(sprite, 0);
  const baseX = sprite._baseScaleX ?? 1;
  const baseY = sprite._baseScaleY ?? 1;
  const flipSign = inner.scale.x < 0 ? -1 : 1;
  inner.scale.x = flipSign * baseX;
  inner.scale.y = baseY;
  inner.alpha = 1;
  if (sprite._blossomOverlay?._inner) {
    sprite._blossomOverlay._inner.alpha = 0;
  }
}

export function settleEntranceSprites(sprites) {
  if (!Array.isArray(sprites)) return;
  for (const sprite of sprites) restoreSpriteAtRest(sprite);
}

function shouldExitEntrance(sprite, cancel) {
  if (cancel?.cancelled) return true;
  if (cancel?.fastForwarded) {
    restoreSpriteAtRest(sprite);
    return true;
  }
  return false;
}

function pathDurationMs(x1, y1, x2, y2, pxPerSec) {
  const dist = Math.hypot(x2 - x1, y2 - y1);
  return (dist / Math.max(1, pxPerSec)) * 1000;
}

// --- Sprite positioning, flipping, rotation -----------------------------
//
// The animation engine operates on "objects" which are Container wrappers
// (built in scene.js).  The container's (x, y) is the object's visual
// top-left; flipping and rotating the inner sprite (anchor 0.5, 0.5 at
// container-local (w/2, h/2)) leaves the container's position untouched
// and the visual bounding box unchanged.
//
// Helper resolves a direct sprite (old code path / simple tests) vs a
// container wrapper by checking for the _inner property.

function getInner(obj) { return obj._inner || obj; }

/**
 * Set the object's visual top-left to (logicalX, logicalY).
 */
export function setSpriteLogicalPos(obj, logicalX, logicalY) {
  obj.x = logicalX;
  obj.y = logicalY;
}

/**
 * Set the sprite's horizontal facing.  `nativeFacing` is which way the
 * sprite image is drawn when scale.x is positive.  Flipping mirrors the
 * inner sprite around its own anchor (0.5, 0.5 = its visual center),
 * so the container's position doesn't shift.
 *
 * Returns true iff the flip state actually changed.
 */
export function setFacingRight(obj, facingRight, nativeFacing = 'left') {
  const inner = getInner(obj);
  const currentFacingNative = inner.scale.x >= 0;
  const wantFacingNative = (nativeFacing === 'left') ? !facingRight : facingRight;
  if (currentFacingNative === wantFacingNative) return false;
  inner.scale.x = wantFacingNative ? 1 : -1;
  return true;
}

/**
 * Set the rotation of the inner sprite in radians.  Rotation pivots
 * around the sprite's visual center so the container's position is
 * unaffected.
 */
export function setSpriteRotation(obj, radians) {
  const inner = getInner(obj);
  inner.rotation = radians;
}

/**
 * Assign a PIXI Texture to the inner sprite (cycle frame swap).
 */
export function setSpriteTexture(obj, texture) {
  const inner = getInner(obj);
  inner.texture = texture;
}

// --- Cycle (keyframe) sampling ------------------------------------------

/**
 * Compute a cycle's natural duration: the largest (t + lastFrameDuration)
 * across all keyframes (or the largest t if no lastFrameDuration).
 */
export function cycleNaturalDuration(keyframes) {
  let max = 0;
  for (const k of keyframes) {
    const end = k.t + (k.lastFrameDuration || 0);
    if (end > max) max = end;
  }
  return max;
}

/**
 * Look up the keyframe state at cycle-time `t`.  Between keyframes,
 * deltaX/deltaY are linearly interpolated and the image is a step
 * function (prev keyframe's image holds until the next one's t).
 *
 * Rotation (`rotatePosDegrees`) uses a SEPARATE keyframe track: only
 * frames that explicitly declare `rotatePosDegrees` are rotation
 * keyframes.  An implicit identity (0 degrees) is inserted at t=0 and
 * at t=cycleEnd if no explicit rotation keyframe occupies those times,
 * so the sprite always returns to zero rotation at both ends of the
 * cycle.  This mirrors MKSoundCoordinatedAnimationLayer.m lines 998-999
 * (initial identity) and 1118-1121 (trailing identity at last key).
 *
 * @param {Array} keyframes
 * @param {number} t           Cycle-relative time (seconds)
 * @param {number} [cycleEnd]  Cycle natural duration (defaults to
 *                             cycleNaturalDuration(keyframes))
 * @returns {{ image: string|null, deltaX: number, deltaY: number, rotationDeg: number }}
 */
export function sampleCycle(keyframes, t, cycleEnd = null) {
  if (keyframes.length === 0) {
    return { image: null, deltaX: 0, deltaY: 0, rotationDeg: 0, scaleX: 1, scaleY: 1, alpha: 1 };
  }
  if (cycleEnd == null) cycleEnd = cycleNaturalDuration(keyframes);

  // --- Image track (step function by keyframe order) ---
  let prev = keyframes[0];
  for (let i = 0; i < keyframes.length; i++) {
    const k = keyframes[i];
    if (k.t <= t) {
      prev = k;
    } else {
      break;
    }
  }
  const image = prev.image || null;

  // --- Translation tracks (deltaX / deltaY).
  //
  // Like the scale/rotation/alpha tracks, these are sparse: only
  // keyframes that explicitly declare the field participate, and
  // implicit identity (0) is padded at t=0 and t=cycleEnd if the
  // extremes are absent.  This lets an author declare just the
  // anchor values that matter (e.g. flowers grow: deltaY at t=0
  // and t=0.8 only) without image-only frames in between collapsing
  // the track.
  const deltaX = sampleDeltaTrack(keyframes, t, cycleEnd, 'deltaX');
  const deltaY = sampleDeltaTrack(keyframes, t, cycleEnd, 'deltaY');

  // --- Rotation track (separate keyframe list, implicit identity at
  //     t=0 and t=cycleEnd if not explicitly set) ---
  const rotationDeg = sampleRotationTrack(keyframes, t, cycleEnd);

  // --- Scale tracks (per-axis).  Like rotation, only frames that
  // explicitly declare a scale value participate; identity (1.0) is
  // padded at t=0 and t=cycleEnd if absent so the sprite starts and
  // ends at its natural scale.
  const scaleX = sampleScaleTrack(keyframes, t, cycleEnd, 'scaleX');
  const scaleY = sampleScaleTrack(keyframes, t, cycleEnd, 'scaleY');

  // --- Alpha track -- fades in/out via keyframe opacity values.
  // Semantics match scale/rotation: only frames that declare `alpha`
  // participate, with implicit 1.0 at t=0 and t=cycleEnd.  Used by
  // apple grow (0.5 → 1.0), apple flower fade-out, etc.
  const alpha = sampleScaleTrack(keyframes, t, cycleEnd, 'alpha');

  return { image, deltaX, deltaY, rotationDeg, scaleX, scaleY, alpha };
}

/**
 * Linearly interpolate a sparse translation-delta track (`deltaX` or
 * `deltaY`).  Only keyframes that explicitly declare the field
 * participate; the track is padded with identity (0) at t=0 and
 * t=cycleEnd when the authored extremes are absent, so a cycle with
 * no authored deltas is a flat zero, and a cycle with a single
 * anchor point ramps smoothly from zero to that anchor and back.
 * Supports legacy string values (e.g. "12.5") via parseFloat.
 */
function sampleDeltaTrack(keyframes, t, cycleEnd, key) {
  const pts = [];
  let hasZero = false, hasEnd = false;
  for (const k of keyframes) {
    const raw = k[key];
    if (raw === undefined || raw === null) continue;
    const v = typeof raw === 'string' ? parseFloat(raw) : raw;
    pts.push({ t: k.t, v });
    if (k.t === 0) hasZero = true;
    if (k.t === cycleEnd) hasEnd = true;
  }
  if (pts.length === 0) return 0;
  if (!hasZero) pts.unshift({ t: 0, v: 0 });
  if (!hasEnd) pts.push({ t: cycleEnd, v: 0 });
  const ct = Math.max(0, Math.min(cycleEnd, t));
  let pPrev = pts[0];
  let pNext = pts[pts.length - 1];
  for (let i = 0; i < pts.length; i++) {
    const p = pts[i];
    if (p.t <= ct) {
      pPrev = p;
      pNext = pts[i + 1] || p;
    } else {
      pNext = p;
      break;
    }
  }
  if (pNext.t === pPrev.t) return pPrev.v;
  const frac = (ct - pPrev.t) / (pNext.t - pPrev.t);
  return pPrev.v + (pNext.v - pPrev.v) * frac;
}

/**
 * Linearly interpolate a per-axis scale track.  Supports JSON values
 * that arrive as strings (some legacy plist entries were stringly-
 * typed, e.g. "0.25") by parseFloat-ing them.
 */
function sampleScaleTrack(keyframes, t, cycleEnd, key) {
  const pts = [];
  let hasZero = false, hasEnd = false;
  for (const k of keyframes) {
    const raw = k[key];
    if (raw === undefined || raw === null) continue;
    const v = typeof raw === 'string' ? parseFloat(raw) : raw;
    pts.push({ t: k.t, v });
    if (k.t === 0) hasZero = true;
    if (k.t === cycleEnd) hasEnd = true;
  }
  if (pts.length === 0) return 1.0;
  if (!hasZero) pts.unshift({ t: 0, v: 1.0 });
  if (!hasEnd) pts.push({ t: cycleEnd, v: 1.0 });
  const ct = Math.max(0, Math.min(cycleEnd, t));
  let pPrev = pts[0];
  let pNext = pts[pts.length - 1];
  for (let i = 0; i < pts.length; i++) {
    const p = pts[i];
    if (p.t <= ct) {
      pPrev = p;
      pNext = pts[i + 1] || p;
    } else {
      pNext = p;
      break;
    }
  }
  if (pNext.t === pPrev.t) return pPrev.v;
  const frac = (ct - pPrev.t) / (pNext.t - pPrev.t);
  return pPrev.v + (pNext.v - pPrev.v) * frac;
}

/**
 * Sample the rotation track at time `t`.  Only frames with
 * `rotatePosDegrees` participate.  The track is padded with identity
 * (0 degrees) at t=0 and t=cycleEnd so the sprite starts and ends
 * unrotated.
 *
 * @returns {number} degrees
 */
function sampleRotationTrack(keyframes, t, cycleEnd) {
  // Collect rotation-bearing keyframes, then add implicit endpoints.
  let hasZero = false;
  let hasEnd = false;
  const rots = [];
  for (const k of keyframes) {
    if (k.rotatePosDegrees === undefined || k.rotatePosDegrees === null) continue;
    rots.push({ t: k.t, deg: k.rotatePosDegrees });
    if (k.t === 0) hasZero = true;
    if (k.t === cycleEnd) hasEnd = true;
  }
  if (rots.length === 0) return 0;   // no rotation on this cycle

  if (!hasZero) rots.unshift({ t: 0, deg: 0 });
  if (!hasEnd) {
    // Use the nearest visual-equivalent of 0° to the last authored
    // rotation so the interpolation doesn't reverse through a full
    // revolution.  E.g. 360° → 360° (same as 0° visually) instead
    // of 360° → 0° which spins the sprite backwards.
    const lastDeg = rots[rots.length - 1].deg;
    const endDeg = Math.round(lastDeg / 360) * 360;
    rots.push({ t: cycleEnd, deg: endDeg });
  }

  // Clamp t to [0, cycleEnd] for safety, then linearly interpolate.
  const ct = Math.max(0, Math.min(cycleEnd, t));
  let pPrev = rots[0];
  let pNext = rots[rots.length - 1];
  for (let i = 0; i < rots.length; i++) {
    const r = rots[i];
    if (r.t <= ct) {
      pPrev = r;
      pNext = rots[i + 1] || r;
    } else {
      pNext = r;
      break;
    }
  }
  if (pNext.t === pPrev.t) return pPrev.deg;
  const frac = (ct - pPrev.t) / (pNext.t - pPrev.t);
  return pPrev.deg + (pNext.deg - pPrev.deg) * frac;
}

// --- Texture preloading -------------------------------------------------

/**
 * Load every unique cycle-frame image referenced by an animConfig for
 * the active variant and return a { imagePath -> Texture } map.
 * Returns an empty map if the theme has no cycles or the textures are
 * unavailable (e.g. in tests where Assets is mocked away).
 */
export async function preloadCycleTextures(animConfig, variant) {
  const cycles = animConfig.cycles || {};
  const paths = new Set();
  for (const name in cycles) {
    const frames = cycles[name]?.[variant] || [];
    for (const f of frames) if (f.image) paths.add(f.image);
  }
  const map = {};
  for (const p of paths) {
    try {
      const alias = '/' + p;
      // Cache-aware: if this alias is already in the Assets cache,
      // reuse it directly rather than calling Assets.add (which would
      // log a noisy "already has key: X overwriting" warning on every
      // repeat call -- common when buildCurrentScene is called twice
      // in a session, or when this preload runs both upstream and
      // inside runEntrance).
      const cached = Assets && typeof Assets.get === 'function'
        ? Assets.get(alias) : null;
      if (cached) { map[p] = cached; continue; }
      if (Assets && typeof Assets.add === 'function') Assets.add({ alias, src: alias });
      if (Assets && typeof Assets.load === 'function') {
        map[p] = await Assets.load(alias);
      }
    } catch {
      /* best effort -- skip failed loads so other frames still work */
    }
  }
  return map;
}

/**
 * Collect the relative paths of every sound file a cycle references
 * for the given variant.  Used by runEntrance to preload sounds.
 * Picks up both `soundFile` (single) and `soundFileList` (array --
 * one element will be chosen at random when the keyframe fires, but
 * all candidates must be preloaded so every possible pick is ready).
 */
export function listCycleSounds(animConfig, variant) {
  const cycles = animConfig.cycles || {};
  const paths = new Set();
  for (const name in cycles) {
    const frames = cycles[name]?.[variant] || [];
    for (const f of frames) {
      if (f.soundFile) paths.add(f.soundFile);
      if (Array.isArray(f.soundFileList)) {
        for (const p of f.soundFileList) paths.add(p);
      }
    }
  }
  return Array.from(paths);
}

/**
 * Collect every sound URL referenced by an entrance animation -- cycle
 * keyframe soundFiles + any theme-level jumpSound.  URLs are returned
 * with a leading "/" matching playOneShot/preload usage.  Does NOT
 * include the music loop (that starts via playLoop which loads
 * immediately; the fade-in already masks its load latency).
 */
export function collectSceneSoundUrls(animConfig, variant) {
  const out = new Set();
  for (const p of listCycleSounds(animConfig, variant)) {
    out.add('/' + p);
  }
  if (animConfig?.jumpSound) out.add('/' + animConfig.jumpSound);
  return Array.from(out);
}

// --- Generic cycle playback over a linear step -------------------------

/**
 * Animate a single step: the sprite moves in a straight line from
 * (x1,y1) to (x2,y2) over durationMs.  On top of that linear motion,
 * a keyframe cycle drives texture swaps and deltaX/Y offsets.
 *
 * When `keyframes` is empty, the step is a plain straight-line slide.
 *
 * @param {{
 *   sprite: any,
 *   x1: number, y1: number, x2: number, y2: number,
 *   durationMs: number,
 *   keyframes?: Array,
 *   textureMap?: Object,
 *   nativeFacing?: 'left'|'right',
 *   facingRight?: boolean,
 *   cancel?: { cancelled: boolean },
 * }} opts
 */
export async function runStepWithCycle(opts) {
  const { sprite, x1, y1, x2, y2, durationMs,
          keyframes = [], textureMap = {},
          nativeFacing = 'left', facingRight = null,
          cycles = 1,                 // how many cycle repetitions during this step
          soundLeadTimeMs = 0,        // fire soundFiles this many ms early
                                      // (to compensate for audio pipeline latency)
          phaseTiltDeg = 0,           // one-shot lean across the WHOLE step,
                                      // overlayed on top of cycle rotation.
                                      // 0->phaseTiltDeg at t=0.25, hold to
                                      // t=0.75, back to 0 by t=1.0 -- matches
                                      // the legacy rotation keyframe pattern.
          cancel } = opts;

  // Set facing (if caller wants it locked before the step starts)
  if (facingRight !== null) setFacingRight(sprite, facingRight, nativeFacing);
  const inner = getInner(sprite);
  const flipped = inner.scale.x < 0;

  // Track the playing frame so we only swap the texture when the frame
  // actually changes -- cuts down on unnecessary property writes.
  let lastImage = null;
  const firedSounds = new Set();

  const cycleTime = cycleNaturalDuration(keyframes);
  const hasCycle = keyframes.length > 0 && cycleTime > 0;
  // Total cycle playback time -- the cycle itself gets stretched or
  // repeated across the step duration.  When `cycles` > 1 the cycle
  // replays that many times within the step.
  const totalCycleSpan = cycleTime * Math.max(1, cycles);

  await animateOver(durationMs, (t) => {
    const baseX = lerp(x1, x2, t);
    const baseY = lerp(y1, y2, t);

    let offsetX = 0, offsetY = 0, cycleRotDeg = 0;
    let cycleScaleX = 1, cycleScaleY = 1, cycleAlpha = 1;
    if (hasCycle) {
      // Map step-time t to cycle-time.  Modulo lets cycles > 1 loop
      // cleanly across cycle boundaries, BUT at the very last frame
      // (t >= 1) modulo wraps to 0 -- which would render the FIRST
      // keyframe instead of the last, producing a visible
      // "first-frame flash" at the end of any single-cycle phase
      // (noticed on the apple blossom fade-out).  Clamp to cycleTime
      // at the end so the final render shows the final keyframe.
      const totalT = t * totalCycleSpan;
      const cycleT = (t >= 1) ? cycleTime : (totalT % cycleTime);
      const sample = sampleCycle(keyframes, cycleT, cycleTime);
      // Mirror deltaX when flipped so body motion reads as "forward in
      // the direction of travel."
      offsetX = flipped ? -sample.deltaX : sample.deltaX;
      offsetY = sample.deltaY;
      // Cycle rotation is mirrored when flipped to match the sheep-
      // jump arc code's inlined behavior (runFenceJump phase 2).
      // Keeping this consistent avoids surprise if a future cycle
      // with rotation keyframes is played on a flipped sprite; no
      // current content exercises this path (chickHop has no
      // rotation, eweTrot has no rotation, eweJump is played only on
      // unflipped sprites).
      cycleRotDeg = flipped ? -sample.rotationDeg : sample.rotationDeg;
      cycleScaleX = sample.scaleX;
      cycleScaleY = sample.scaleY;
      cycleAlpha = sample.alpha;
      if (sample.image && sample.image !== lastImage && textureMap[sample.image]) {
        setSpriteTexture(sprite, textureMap[sample.image]);
        lastImage = sample.image;
      }
      // Fire per-frame sounds once per pass through the cycle.  With
      // cycles > 1 we reset firedSounds at each cycle boundary so
      // sounds trigger once per repetition.  soundLeadTimeMs fires
      // each sound early by N ms of step-wall-time to compensate for
      // audio-pipeline latency between play() call and audible output.
      // Converted into cycle-time units: leadCycle = leadMs * cycleTime / stepDuration.
      //
      // IMPORTANT: clamp cycleIdx to `cycles - 1` at the step end to
      // mirror the cycleT clamp above.  Without this clamp, at t=1
      // Math.floor(totalT / cycleTime) returns `cycles` (one past the
      // last cycle's index) and the firedSounds Set sees a new key --
      // causing the t=0 keyframe to fire a SECOND time at step end,
      // since cycleT=cycleTime (clamped) still satisfies the
      // `cycleT >= k.t - leadCycle` trigger condition for t=0.
      const cycleIdx = (t >= 1)
        ? Math.max(0, Math.max(1, cycles) - 1)
        : Math.floor(totalT / cycleTime);
      const leadCycle = soundLeadTimeMs > 0 && durationMs > 0
        ? (soundLeadTimeMs * cycleTime) / durationMs
        : 0;
      for (const k of keyframes) {
        const hasSingle = !!k.soundFile;
        const hasList   = Array.isArray(k.soundFileList) && k.soundFileList.length > 0;
        if (!hasSingle && !hasList) continue;
        const soundKey = `${cycleIdx}:${k.t}`;
        if (cycleT >= (k.t - leadCycle) && !firedSounds.has(soundKey)) {
          firedSounds.add(soundKey);
          // soundFileList: pick one at random per firing so the same
          // keyframe can produce variety across objects/cycles.  If
          // both `soundFile` and `soundFileList` are present the
          // single file wins (explicit > random).
          let src;
          if (hasSingle) {
            src = k.soundFile;
          } else {
            src = k.soundFileList[Math.floor(Math.random() * k.soundFileList.length)];
          }
          playOneShot('/' + src);
        }
      }
    }

    // Overlay the one-shot phase tilt (legacy 4-keytime pattern).
    //
    // phaseTiltDeg is NOT mirrored when the sprite is flipped.  Unlike
    // cycle-rotation keyframes (which are authored against the
    // sprite's native facing and need flipping to preserve intent),
    // phaseTiltDeg is computed by the caller directly from the
    // world-space travel direction (see the cow field phase's
    // atan(yDist/xDist) calculation).  Mirroring it here would double-
    // invert the sign and rotate the cow the wrong way.  Tested
    // empirically during the 2026-04 cow work.
    let phaseTiltForFrame = 0;
    if (phaseTiltDeg !== 0) {
      phaseTiltForFrame = phaseTiltRamp(t, phaseTiltDeg);
    }

    const totalRotDeg = cycleRotDeg + phaseTiltForFrame;
    setSpriteLogicalPos(sprite, baseX + offsetX, baseY + offsetY);
    setSpriteRotation(sprite, totalRotDeg * Math.PI / 180);
    // Apply cycle scale (flowerGrow, appleGrow).  Flip is preserved
    // by multiplying with the sign of the flip -- a flipped sprite
    // with a 0.25 cycle scale gets scale.x = -0.25, keeping it
    // mirrored and shrunk.  If the cycle has no scale keyframes,
    // sample returns scaleX=scaleY=1 so flip is unchanged.
    if (hasCycle) {
      // Cycle scale keyframes MULTIPLY against the base scale PIXI
      // set in scene.js when innerSprite.width/height were assigned.
      // Without this multiplication, cycle code would overwrite the
      // base scale with 1.0 (for cycles without scale keyframes) or
      // a raw cycle value (e.g. 1.0 at end of grow), causing sprites
      // to render at their full TEXTURE size instead of the theme's
      // configured display size.
      const baseX = sprite._baseScaleX ?? 1;
      const baseY = sprite._baseScaleY ?? 1;
      const flipSign = flipped ? -1 : 1;
      const xs = flipSign * baseX * cycleScaleX;
      const ys = baseY * cycleScaleY;
      if (inner.scale.x !== xs) inner.scale.x = xs;
      if (inner.scale.y !== ys) inner.scale.y = ys;
      if (inner.alpha !== cycleAlpha) inner.alpha = cycleAlpha;
    }
  }, cancel);

  // Restore the static texture, identity rotation, BASE scale
  // (preserving flip sign on x), and full opacity so the resting
  // pose matches what scene.js set at build time.
  if (hasCycle) {
    if (sprite._staticTexture) setSpriteTexture(sprite, sprite._staticTexture);
    const flipSign = flipped ? -1 : 1;
    const baseX = sprite._baseScaleX ?? 1;
    const baseY = sprite._baseScaleY ?? 1;
    inner.scale.x = flipSign * baseX;
    inner.scale.y = baseY;
    inner.alpha = 1;
  }
  // Always reset rotation at step end -- belt-and-suspenders in case the
  // phase tilt was applied without a cycle.
  setSpriteRotation(sprite, 0);
}

/**
 * Ramp the legacy 4-keytime tilt pattern: 0 at t=0, full `tiltDeg` at
 * t=0.25, hold through t=0.75, ramp back to 0 by t=1.0.  Linearly
 * interpolates between the four control points.  Matches the
 * CAKeyframeAnimation keyTimes=[0, 0.25, 0.75, 1] in the legacy
 * CoopRampPointAnimationOperation.
 */
export function phaseTiltRamp(t, tiltDeg) {
  if (t <= 0) return 0;
  if (t >= 1) return 0;
  if (t < 0.25) return tiltDeg * (t / 0.25);
  if (t < 0.75) return tiltDeg;
  return tiltDeg * (1 - (t - 0.75) / 0.25);
}

/**
 * Compute the body-lean angle (degrees) for the cow-style field phase
 * based on the path from ramp exit to final rest position.
 *
 * Legacy CoopRampPointAnimationOperation applies `atan(yDist/xDist)`
 * rotation, capped at ±15°, so the cow leans into its direction of
 * travel (nose up when heading up-slope, nose down when heading
 * down-slope).  We keep that formula, then apply two dead zones per
 * the cow-scene spec (2026-04):
 *   - Mostly HORIZONTAL paths (slope below HORIZONTAL_DEADZONE_DEG):
 *     no rotation.  A small atan value would only produce a tiny
 *     tilt that's visually noise.
 *   - Mostly VERTICAL paths (slope above VERTICAL_DEADZONE_DEG):
 *     no rotation.  A cow walking nearly straight down out of the
 *     barn looks wrong with a 15° lean.
 *
 * Distance scaling: to keep short walks subtle (they rotate quickly
 * due to the fractional-time phaseTiltRamp, and the full 15° tilt
 * magnifies the snap), the tilt is scaled by
 * `min(1, |xDist| / referenceDistance)`.  Note: horizontal distance
 * is used (not euclidean) because the "walking up/down a hill" feel
 * is driven by horizontal traversal; a very short xDist with a large
 * yDist is already filtered out by the vertical dead zone.  A
 * reference distance of e.g. 300 design-pixels means a 100-px
 * horizontal walk gets 1/3 the tilt of a 300+-px horizontal walk.
 * Pass `referenceDistance = 0` or `Infinity` to disable scaling.
 *
 * @param {number} xDist  finalX - rampExitX (design pixels)
 * @param {number} yDist  finalY - rampExitY (design pixels)
 * @param {number} [referenceDistance]  Walk distance at which full
 *                tilt is applied.  Shorter walks scale down linearly.
 *                Defaults to Infinity (no scaling).
 * @returns {number} degrees, positive = CW (nose-down for rightward
 *                   travel), zero in either dead zone.
 */
export function computeFieldTiltDeg(xDist, yDist, referenceDistance = Infinity) {
  const FIELD_TILT_CAP_DEG = 15;
  const HORIZONTAL_DEADZONE_DEG = 10;
  const VERTICAL_DEADZONE_DEG = 70;
  if (Math.abs(xDist) < 1e-3) return 0;  // pure vertical
  // Absolute slope angle (0° = horizontal, 90° = vertical).
  const slopeDeg = Math.atan2(Math.abs(yDist), Math.abs(xDist)) * 180 / Math.PI;
  if (slopeDeg < HORIZONTAL_DEADZONE_DEG) return 0;
  if (slopeDeg > VERTICAL_DEADZONE_DEG) return 0;
  let tiltDeg = Math.atan(yDist / xDist) * 180 / Math.PI;
  if (tiltDeg > FIELD_TILT_CAP_DEG) tiltDeg = FIELD_TILT_CAP_DEG;
  if (tiltDeg < -FIELD_TILT_CAP_DEG) tiltDeg = -FIELD_TILT_CAP_DEG;
  // Distance scaling: short walks get proportionally less tilt so the
  // quick in-and-out rotation (caused by fractional phaseTiltRamp
  // timing) stays visually subtle.  Uses HORIZONTAL distance only --
  // the lean-into-hill feel tracks how much ground the cow covers
  // left-to-right, not total path length.
  if (referenceDistance > 0 && isFinite(referenceDistance)) {
    const scale = Math.min(1, Math.abs(xDist) / referenceDistance);
    tiltDeg *= scale;
  }
  return tiltDeg;
}

// --- Fence-jump sequence ------------------------------------------------

/**
 * @param {import('pixi.js').Container} sprite   A scene object container
 * @param {{
 *   startX, startY, jumpStartX, jumpStartY, jumpEndX, jumpEndY,
 *   finalX, finalY, velocity, jumpSoundSrc,
 *   arcHeightScale, airSpeedMultiplier,
 *   nativeFacing,
 *   walkKeyframes, walkCycleWidth,
 *   jumpKeyframes,
 *   textureMap,
 * }} cfg
 * @param {{ cancelled: boolean }} [cancel]
 */
export async function runFenceJump(sprite, cfg, cancel) {
  const {
    startX, startY, jumpStartX, jumpStartY, jumpEndX, jumpEndY,
    finalX, finalY, velocity, jumpSoundSrc,
    arcHeightScale = 0.35,
    airSpeedMultiplier = 1.5,
    nativeFacing = 'left',
    walkKeyframes = [], walkCycleWidth = 0,
    jumpKeyframes = [],
    textureMap = {},
    soundLeadTimeMs = 0,
  } = cfg;

  // Phase 1: walk to fence.  The trot cycle cycles `cycleCount` times
  // during this walk, where cycleCount = distance / walkCycleWidth.
  // This matches the legacy pattern: one full leg-cycle per N design
  // pixels of ground travel.
  setSpriteLogicalPos(sprite, startX, startY);
  const leg1Dist = Math.hypot(jumpStartX - startX, jumpStartY - startY);
  const leg1Dur = pathDurationMs(startX, startY, jumpStartX, jumpStartY, velocity);
  const leg1Cycles = walkCycleWidth > 0 ? Math.max(1, Math.round(leg1Dist / walkCycleWidth)) : 1;
  await runStepWithCycle({
    sprite,
    x1: startX, y1: startY, x2: jumpStartX, y2: jumpStartY,
    durationMs: leg1Dur,
    keyframes: walkKeyframes, textureMap,
    nativeFacing,
    facingRight: jumpStartX > startX,
    cycles: leg1Cycles,
    soundLeadTimeMs,
    cancel,
  });
  if (shouldExitEntrance(sprite, cancel)) return;

  // Phase 2: arc over the fence.  The jump cycle plays ONCE over the
  // entire arc -- the cycle's natural duration is stretched/compressed
  // to match the arc duration.  Jump sound comes from the cycle's
  // keyframes (fired in runStepWithCycle) if configured; a separate
  // jumpSoundSrc is fired additionally if no in-cycle soundFile is set.
  setFacingRight(sprite, jumpEndX > jumpStartX, nativeFacing);
  const jumpHasSound = jumpKeyframes.some((k) => k.soundFile);
  if (jumpSoundSrc && !jumpHasSound) playOneShot(jumpSoundSrc);
  const arcDist = Math.hypot(jumpEndX - jumpStartX, jumpEndY - jumpStartY);
  const arcDuration = (arcDist / Math.max(1, velocity * airSpeedMultiplier)) * 1000;
  const arcHeight = arcDist * arcHeightScale;
  // Pre-compute a "cycle overlay" by layering the jump-cycle deltas on
  // top of the parabolic arc trajectory.  We can't use runStepWithCycle
  // directly here because it moves linearly from start to end; we need
  // an arc for the base motion.  Instead we inline an animateOver that
  // samples the cycle and the parabolic arc together.
  {
    const inner = getInner(sprite);
    const flipped = inner.scale.x < 0;
    const jumpCycleTime = cycleNaturalDuration(jumpKeyframes);
    const firedSounds = new Set();
    let lastImage = null;
    await animateOver(arcDuration, (t) => {
      const e = easeInOut(t);
      const bx = lerp(jumpStartX, jumpEndX, e);
      const by = lerp(jumpStartY, jumpEndY, e) - 4 * arcHeight * t * (1 - t);
      let offX = 0, offY = 0, rotRad = 0;
      if (jumpKeyframes.length > 0 && jumpCycleTime > 0) {
        const cycleT = t * jumpCycleTime;
        const s = sampleCycle(jumpKeyframes, cycleT, jumpCycleTime);
        offX = flipped ? -s.deltaX : s.deltaX;
        offY = s.deltaY;
        rotRad = (flipped ? -s.rotationDeg : s.rotationDeg) * Math.PI / 180;
        if (s.image && s.image !== lastImage && textureMap[s.image]) {
          setSpriteTexture(sprite, textureMap[s.image]);
          lastImage = s.image;
        }
        // soundLeadTimeMs fires each sound early by N ms of arc wall
        // time, converted to cycle-time units so the shift is applied
        // against each keyframe's k.t correctly.
        const leadCycle = soundLeadTimeMs > 0 && arcDuration > 0
          ? (soundLeadTimeMs * jumpCycleTime) / arcDuration
          : 0;
        for (const k of jumpKeyframes) {
          if (!k.soundFile) continue;
          if (cycleT >= (k.t - leadCycle) && !firedSounds.has(k.t)) {
            firedSounds.add(k.t);
            playOneShot('/' + k.soundFile);
          }
        }
      }
      setSpriteLogicalPos(sprite, bx + offX, by + offY);
      setSpriteRotation(sprite, rotRad);
    }, cancel);
    // Restore static texture + zero rotation after the arc cycle.
    if (jumpKeyframes.length > 0) {
      if (sprite._staticTexture) setSpriteTexture(sprite, sprite._staticTexture);
      setSpriteRotation(sprite, 0);
    }
  }
  if (shouldExitEntrance(sprite, cancel)) return;

  // Phase 3: walk to final with trot cycle.
  const leg3Dist = Math.hypot(finalX - jumpEndX, finalY - jumpEndY);
  const leg3Dur = pathDurationMs(jumpEndX, jumpEndY, finalX, finalY, velocity);
  const leg3Cycles = walkCycleWidth > 0 ? Math.max(1, Math.round(leg3Dist / walkCycleWidth)) : 1;
  await runStepWithCycle({
    sprite,
    x1: jumpEndX, y1: jumpEndY, x2: finalX, y2: finalY,
    durationMs: leg3Dur,
    keyframes: walkKeyframes, textureMap,
    nativeFacing,
    facingRight: finalX > jumpEndX,
    cycles: leg3Cycles,
    soundLeadTimeMs,
    cancel,
  });
  if (shouldExitEntrance(sprite, cancel)) return;
  setSpriteLogicalPos(sprite, finalX, finalY);
}

// --- Coop-ramp sequence -------------------------------------------------

/**
 * Three-phase entrance matching the legacy HopStepAnimationOperation,
 * but driven by a data-defined keyframe cycle (stepCycle) that plays
 * once per step in each phase.
 *
 * Timings per phase come from tuning.phases.{barn,ramp,field}, where:
 *   stepWidthScale  -- stride length as a fraction of the theme hopWidth
 *   cycleTimeScale  -- how long each cycle plays relative to step duration
 *
 * @param {import('pixi.js').Sprite} sprite
 * @param {{
 *   startX, startY, rampPoints, finalX, finalY,
 *   velocity, rampVelocity,
 *   hopWidth, stepCycle,
 *   keyframes, textureMap,
 *   nativeFacing, tuning,
 * }} cfg
 * @param {{ cancelled: boolean }} [cancel]
 */
export async function runCoopRamp(sprite, cfg, cancel) {
  const {
    startX, startY, rampPoints, finalX, finalY,
    velocity, rampVelocity,
    hopWidth = 0,
    withHop = true,
    keyframes = [], textureMap = {},
    nativeFacing = 'left',
    tuning = {},
    soundLeadTimeMs = 0,
    fieldTiltReferenceDist = Infinity,
  } = cfg;
  const phaseTuning = tuning.phases || {
    barn:  { stepWidthScale: 1.0, cycleTimeScale: 1.0 },
    ramp:  { stepWidthScale: 1.0, cycleTimeScale: 1.0 },
    field: { stepWidthScale: 1.0, cycleTimeScale: 1.0 },
  };

  // withHop=true (chicks): each phase is broken into DISCRETE HOP steps of
  //                       length `hopWidth`, each playing the cycle once.
  //                       Matches legacy HopStepAnimationOperation.
  // withHop=false (cows): each phase is a SINGLE continuous step with the
  //                      cycle looping N times where N is derived from
  //                      phase-wall-time / cycle-natural-duration.  Matches
  //                      legacy CoopRampPointAnimationOperation where the
  //                      walk-cycle animation plays at its natural rate
  //                      regardless of movement velocity.
  const cycleNaturalSec = cycleNaturalDuration(keyframes);
  const useContinuousWalk = !withHop && keyframes.length > 0 && cycleNaturalSec > 0;

  // Compute loop count for a continuous-walk phase given its wall-clock
  // duration.  Minimum 1 so a short phase still plays one cycle.
  function cyclesForPhase(durationMs) {
    if (!useContinuousWalk) return 1;
    return Math.max(1, Math.round(durationMs / (cycleNaturalSec * 1000)));
  }

  // Phase 0 (barn): start -> first rung
  if (rampPoints.length > 0) {
    const [rx0, ry0] = rampPoints[0];
    if (useContinuousWalk) {
      const dur = pathDurationMs(startX, startY, rx0, ry0, rampVelocity);
      await runStepWithCycle({
        sprite, x1: startX, y1: startY, x2: rx0, y2: ry0,
        durationMs: dur,
        keyframes, textureMap, nativeFacing,
        facingRight: rx0 > startX,
        cycles: cyclesForPhase(dur),
        soundLeadTimeMs, cancel,
      });
    } else {
      await runMultiStepPhase({
        sprite, x1: startX, y1: startY, x2: rx0, y2: ry0,
        velocity: rampVelocity, hopWidth,
        keyframes, textureMap, nativeFacing,
        tuning: phaseTuning.barn, soundLeadTimeMs, cancel,
      });
    }
    if (shouldExitEntrance(sprite, cancel)) return;
  }

  // Phase 1 (ramp): rung to rung.  For withHop=true each segment is one
  // discrete hop.  For withHop=false we flatten the whole ramp into a
  // single continuous walk across all points (matching legacy
  // CAKeyframeAnimation along the path), with the walk cycle looping N
  // times over the total ramp duration.
  if (useContinuousWalk && rampPoints.length >= 2) {
    // Total ramp distance across all segments
    let totalDist = 0;
    for (let i = 1; i < rampPoints.length; i++) {
      const [a, b] = rampPoints[i - 1];
      const [c, dY] = rampPoints[i];
      totalDist += Math.hypot(c - a, dY - b);
    }
    const totalDur = (totalDist / Math.max(1, rampVelocity)) * 1000;
    const totalCycles = cyclesForPhase(totalDur);
    // Walk each segment linearly.  We split the loop count
    // proportionally to segment distance so the cycle plays smoothly
    // across the whole ramp.
    let distSoFar = 0;
    for (let i = 1; i < rampPoints.length; i++) {
      const [x1, y1] = rampPoints[i - 1];
      const [x2, y2] = rampPoints[i];
      const segDist = Math.hypot(x2 - x1, y2 - y1);
      const segDur = (segDist / Math.max(1, rampVelocity)) * 1000;
      // Proportional fraction of the total loop count for this segment
      const segCycles = Math.max(1, Math.round(totalCycles * (segDist / totalDist)));
      await runStepWithCycle({
        sprite, x1, y1, x2, y2, durationMs: segDur,
        keyframes, textureMap, nativeFacing,
        facingRight: x2 > x1,
        cycles: segCycles,
        soundLeadTimeMs, cancel,
      });
      if (shouldExitEntrance(sprite, cancel)) return;
      distSoFar += segDist;
    }
  } else {
    for (let i = 1; i < rampPoints.length; i++) {
      const [x1, y1] = rampPoints[i - 1];
      const [x2, y2] = rampPoints[i];
      const dur = pathDurationMs(x1, y1, x2, y2, rampVelocity) * (phaseTuning.ramp.cycleTimeScale ?? 1.0);
      await runStepWithCycle({
        sprite, x1, y1, x2, y2, durationMs: dur,
        keyframes, textureMap, nativeFacing,
        facingRight: x2 > x1, soundLeadTimeMs, cancel,
      });
      if (shouldExitEntrance(sprite, cancel)) return;
    }
  }

  // Phase 2 (field): last rung -> final
  const last = rampPoints[rampPoints.length - 1];
  const [lx, ly] = last || [startX, startY];
  if (useContinuousWalk) {
    const dur = pathDurationMs(lx, ly, finalX, finalY, velocity);
    const fieldTiltDeg = computeFieldTiltDeg(
      finalX - lx, finalY - ly,
      fieldTiltReferenceDist,
    );
    await runStepWithCycle({
      sprite, x1: lx, y1: ly, x2: finalX, y2: finalY,
      durationMs: dur,
      keyframes, textureMap, nativeFacing,
      facingRight: finalX > lx,
      cycles: cyclesForPhase(dur),
      phaseTiltDeg: fieldTiltDeg,
      soundLeadTimeMs, cancel,
    });
  } else {
    await runMultiStepPhase({
      sprite, x1: lx, y1: ly, x2: finalX, y2: finalY,
      velocity, hopWidth,
      keyframes, textureMap, nativeFacing,
      tuning: phaseTuning.field, soundLeadTimeMs, cancel,
    });
  }
  if (shouldExitEntrance(sprite, cancel)) return;
  setSpriteLogicalPos(sprite, finalX, finalY);
}

/**
 * Break (x1,y1) -> (x2,y2) into equal-sized steps of `stepWidth =
 * hopWidth * tuning.stepWidthScale` and run the cycle once per step.
 * Matches the legacy `floor(distance / stepWidth)` step count with a
 * minimum of 1.
 */
async function runMultiStepPhase(opts) {
  const { sprite, x1, y1, x2, y2, velocity, hopWidth,
          keyframes, textureMap, nativeFacing, tuning = {},
          soundLeadTimeMs = 0, cancel } = opts;
  const dist = Math.hypot(x2 - x1, y2 - y1);
  if (dist < 1) return;

  const stepWidth = hopWidth * (tuning.stepWidthScale ?? 1.0);
  const effectiveStep = stepWidth > 0 ? stepWidth : dist;
  const steps = Math.max(1, Math.floor(dist / effectiveStep));
  const cycleTimeScale = tuning.cycleTimeScale ?? 1.0;

  for (let i = 0; i < steps; i++) {
    if (shouldExitEntrance(sprite, cancel)) return;
    const tA = i / steps;
    const tB = (i + 1) / steps;
    const sx = lerp(x1, x2, tA);
    const sy = lerp(y1, y2, tA);
    const ex = lerp(x1, x2, tB);
    const ey = lerp(y1, y2, tB);
    const dur = pathDurationMs(sx, sy, ex, ey, velocity) * cycleTimeScale;
    await runStepWithCycle({
      sprite, x1: sx, y1: sy, x2: ex, y2: ey, durationMs: dur,
      keyframes, textureMap, nativeFacing,
      facingRight: ex > sx, soundLeadTimeMs, cancel,
    });
    if (shouldExitEntrance(sprite, cancel)) return;
  }
}

// --- Side-entrance sequence (horses) ------------------------------------

/**
 * Animate a single object for the horses' side-entrance: a straight-
 * line walk from an off-screen side (left or right) to the final
 * resting position.  The walk cycle (`trot`) loops continuously over
 * the walk distance -- same continuous-walk pattern used for cows,
 * matching the legacy PointToPointAnimationOperation which plays the
 * walk cycle at its natural rate regardless of movement velocity.
 *
 * @param {import('pixi.js').Container} sprite
 * @param {{
 *   startX, startY, finalX, finalY, velocity,
 *   keyframes, textureMap,
 *   nativeFacing, soundLeadTimeMs,
 * }} cfg
 * @param {{ cancelled: boolean }} [cancel]
 */
export async function runSideEntrance(sprite, cfg, cancel) {
  const {
    startX, startY, finalX, finalY, velocity,
    keyframes = [], textureMap = {},
    nativeFacing = 'left',
    soundLeadTimeMs = 0,
  } = cfg;

  setSpriteLogicalPos(sprite, startX, startY);

  const dist = Math.hypot(finalX - startX, finalY - startY);
  if (dist < 1) {
    setSpriteLogicalPos(sprite, finalX, finalY);
    return;
  }

  const cycleNaturalSec = cycleNaturalDuration(keyframes);
  const hasCycle = keyframes.length > 0 && cycleNaturalSec > 0;
  const dur = pathDurationMs(startX, startY, finalX, finalY, velocity);
  // Loop the cycle at its natural rate over the walk duration.  One
  // cycle per cycleNaturalSec seconds of wall-clock time, minimum 1.
  const cycles = hasCycle
    ? Math.max(1, Math.round(dur / (cycleNaturalSec * 1000)))
    : 1;

  await runStepWithCycle({
    sprite,
    x1: startX, y1: startY, x2: finalX, y2: finalY,
    durationMs: dur,
    keyframes, textureMap, nativeFacing,
    facingRight: finalX > startX,
    cycles,
    soundLeadTimeMs, cancel,
  });
  if (shouldExitEntrance(sprite, cancel)) return;
  setSpriteLogicalPos(sprite, finalX, finalY);
}

/**
 * For sideEntrance, decide which side (`'left'` or `'right'`) a
 * sprite should enter from based on its final X position relative to
 * the list of mid-point X dividers.  A sprite whose final X is LESS
 * than the nearest mid-point enters from the LEFT; otherwise RIGHT.
 * Matches the legacy SideEntranceAnimationManager's band-based
 * assignment.
 */
export function pickSideForFinalX(finalX, midPointsPx) {
  if (!Array.isArray(midPointsPx) || midPointsPx.length === 0) {
    // No midpoints configured: default to entering from the side the
    // final position is closer to (scene center as tiebreaker).
    return 'left';
  }
  // Use the midpoint closest to finalX as the decision boundary.
  let nearestIdx = 0;
  let nearestDist = Math.abs(finalX - midPointsPx[0]);
  for (let i = 1; i < midPointsPx.length; i++) {
    const d = Math.abs(finalX - midPointsPx[i]);
    if (d < nearestDist) {
      nearestDist = d;
      nearestIdx = i;
    }
  }
  return finalX < midPointsPx[nearestIdx] ? 'left' : 'right';
}

/**
 * Compute entrance order and per-sprite side assignment for
 * sideEntrance themes (horses, cows).
 *
 * 1. A random vertical dividing line is placed in the middle 50% of
 *    the scene (between 25% and 75% of designW).
 * 2. Sprites whose final X is left of the line enter from the left;
 *    those to the right enter from the right.
 * 3. Within each side group, sprites are sorted by horizontal
 *    distance to their final position (farthest first).  Because all
 *    sprites on a side share the same velocity, the farthest-first
 *    order guarantees they never overtake each other.
 * 4. The two sorted groups are interleaved so both sides animate
 *    concurrently: L0, R0, L1, R1, …
 *
 * @returns {{ order: number[], sideMap: Map<number, 'left'|'right'> }}
 */
export function computeSideEntranceOrder(sprites, layout, designW) {
  // Random dividing line in the middle 50% of the scene.
  const dividerX = designW * (0.25 + Math.random() * 0.5);

  // Classify each sprite and compute the horizontal distance it must
  // travel from the entry edge to its final position.
  const left = [];   // { idx, dist }
  const right = [];  // { idx, dist }
  const sideMap = new Map();

  for (let i = 0; i < sprites.length; i++) {
    const fx = sprites[i]._finalX ?? sprites[i].x;
    if (fx < dividerX) {
      // Enters from the left edge (x ≈ 0) → distance is fx itself.
      left.push({ idx: i, dist: fx });
      sideMap.set(i, 'left');
    } else {
      // Enters from the right edge (x ≈ designW) → distance is designW - fx.
      right.push({ idx: i, dist: designW - fx });
      sideMap.set(i, 'right');
    }
  }

  // Sort each group: farthest first (descending distance).
  left.sort((a, b) => b.dist - a.dist);
  right.sort((a, b) => b.dist - a.dist);

  // Interleave the two groups so both sides animate concurrently.
  const order = [];
  const maxLen = Math.max(left.length, right.length);
  for (let i = 0; i < maxLen; i++) {
    if (i < left.length) order.push(left[i].idx);
    if (i < right.length) order.push(right[i].idx);
  }

  return { order, sideMap };
}

// --- Orchestrator -------------------------------------------------------

/**
 * Determine per-sprite entrance order.  Objects whose final position is
 * farthest from the entry anchor depart first; this spreads traffic so
 * trailing objects don't catch up to and overlap leading ones in front
 * of their destination.  Matches the legacy manager's sort behavior
 * (masterFinalPointsList ordering for coopRamp; centroid-of-jumpEnd
 * reference for fenceJump).
 *
 * For coopRamp, the entry anchor is the center of startingZoneRect.
 * For fenceJump, it's the centroid of the jumpEndPoints list (where
 * sheep land after the jump, before walking to their final spots).
 */
export function computeEntranceOrder(sprites, animConfig, layout, designW, designH) {
  const identity = sprites.map((_, i) => i);
  const anchor = entryAnchor(animConfig, layout, designW, designH);
  if (!anchor) return identity;
  return identity.slice().sort((a, b) => {
    const da = distFromAnchor(sprites[a], anchor.x, anchor.y);
    const db = distFromAnchor(sprites[b], anchor.x, anchor.y);
    return db - da;
  });
}

/**
 * Compute the reference point for per-sprite distance-from-entry sorting.
 * Returns null when the animation type has no meaningful anchor, in
 * which case the caller falls back to selection-order.
 */
function entryAnchor(animConfig, layout, designW, designH) {
  if (animConfig.type === 'coopRamp') {
    const zone = layout?.startingZoneRect;
    if (!zone) return null;
    return {
      x: (zone.x + zone.width / 2) * designW,
      y: (zone.y + zone.height / 2) * designH,
    };
  }
  if (animConfig.type === 'fenceJump') {
    const ends = layout?.jumpEndPoints;
    if (!ends || ends.length === 0) return null;
    let sx = 0, sy = 0;
    for (const [px, py] of ends) { sx += px; sy += py; }
    return {
      x: (sx / ends.length) * designW,
      y: (sy / ends.length) * designH,
    };
  }
  return null;
}

function distFromAnchor(sprite, ax, ay) {
  const fx = sprite._finalX ?? sprite.x;
  const fy = sprite._finalY ?? sprite.y;
  return Math.hypot(fx - ax, fy - ay);
}

/**
 * Run the full entrance for a scene: start music, preload cycle
 * textures, stagger objects, fade music out.
 */
export async function runEntrance({
  sprites, animConfig, variant, designW, designH, cancel,
  animationsEnabled,
}) {
  const layout = animConfig[variant];
  const musicCfg = animConfig.music || {};
  const musicHandle = musicCfg.primary
    ? playLoop('/' + musicCfg.primary, {
        volume: musicCfg.volume ?? 0.5,
        fadeIn: musicCfg.fadeInSeconds ?? 0.8,
      })
    : null;

  // "Entrance animations" off path -- snap every sprite visible at
  // its final resting position and skip the staggered keyframe
  // parade entirely.  Music still plays (it's a backdrop for the
  // scene, not part of the entrance animation).  The probe is
  // called once at entry so a mid-round toggle doesn't produce a
  // half-animated scene.
  const snapOnly = typeof animationsEnabled === 'function'
    ? !animationsEnabled()
    : animationsEnabled === false;
  if (snapOnly) {
    for (const sprite of sprites) sprite.visible = true;
    if (musicHandle && musicCfg.fadeOut && !cancel?.cancelled && !cancel?.fastForwarded) {
      await musicHandle.fadeOut(musicCfg.fadeOutSeconds ?? 1.0);
    }
    return { musicHandle };
  }

  // Preload keyframe textures AND sound effects for this theme+variant
  // BEFORE staggered starts.  Textures avoid a stall on first frame
  // swap; sound preloading avoids the "cold-load" latency where the
  // first playback of a sound file (e.g. sheep_baa_jump at the third
  // jump-cycle keyframe) fires later than its keyframe because Howler
  // is still fetching/decoding the MP3.  preloadAudio resolves once
  // every listed Howl has reported `load` (or `loaderror`, to avoid
  // blocking on a missing file).
  const textureMap = await preloadCycleTextures(animConfig, variant);
  const soundUrls = collectSceneSoundUrls(animConfig, variant);
  if (soundUrls.length > 0) await preloadAudio(soundUrls);

  // Entrance order (farthest-first for coopRamp, side-aware for
  // sideEntrance, identity for others).
  let order, sideMap;
  if (animConfig.type === 'sideEntrance') {
    ({ order, sideMap } = computeSideEntranceOrder(sprites, layout, designW));
  } else {
    order = computeEntranceOrder(sprites, animConfig, layout, designW, designH);
    sideMap = null;
  }

  const tasks = order.map((spriteIdx, staggerIdx) => runOneObjectEntrance({
    sprite: sprites[spriteIdx],
    animConfig, layout, variant, designW, designH,
    textureMap,
    staggerIdx, cancel,
    side: sideMap ? sideMap.get(spriteIdx) : undefined,
  }));
  await Promise.all(tasks);

  if (musicHandle && musicCfg.fadeOut && !cancel?.cancelled && !cancel?.fastForwarded) {
    await musicHandle.fadeOut(musicCfg.fadeOutSeconds ?? 1.0);
  }
  return { musicHandle };
}

async function runOneObjectEntrance({ sprite, animConfig, layout, variant, designW, designH, textureMap, staggerIdx, cancel, side }) {
  const tuning = mergeTuning(animConfig, layout);
  // interStaggerDelaySeconds adds per-object randomness: each object
  // gets a random offset in [-max, +max] added to its base delay so
  // entrances don't feel mechanically uniform.
  const interStaggerMax = tuning.interStaggerDelaySeconds ?? 0;
  const jitter = interStaggerMax > 0
    ? (Math.random() * 2 - 1) * interStaggerMax
    : 0;
  const delay = Math.max(0, ((layout.intervalDelay ?? 1.0) * staggerIdx + jitter)) * 1000;
  await waitForDelay(delay, cancel);
  if (cancel?.cancelled) return;
  if (cancel?.fastForwarded) {
    restoreSpriteAtRest(sprite);
    return;
  }

  sprite.visible = true;
  const finalX = sprite._finalX ?? sprite.x;
  const finalY = sprite._finalY ?? sprite.y;
  const nativeFacing = animConfig.nativeFacing ?? 'left';

  if (animConfig.type === 'fenceJump') {
    await runFenceJumpFromConfig({
      sprite, animConfig, layout, variant, tuning,
      finalX, finalY, designW, designH,
      jumpSoundSrc: animConfig.jumpSound ? '/' + animConfig.jumpSound : undefined,
      textureMap,
      nativeFacing, cancel,
    });
  } else if (animConfig.type === 'coopRamp') {
    await runCoopRampFromConfig({
      sprite, animConfig, layout, variant, tuning,
      finalX, finalY, designW, designH,
      textureMap, nativeFacing, cancel,
    });
  } else if (animConfig.type === 'sideEntrance') {
    await runSideEntranceFromConfig({
      sprite, animConfig, layout, variant, tuning,
      finalX, finalY, designW, designH,
      textureMap, nativeFacing, cancel, side,
    });
  } else if (animConfig.type === 'flowerGrow') {
    await runFlowerGrowFromConfig({
      sprite, animConfig, layout, variant, tuning,
      finalX, finalY,
      textureMap, nativeFacing, cancel,
    });
  } else if (animConfig.type === 'appleGrowAndDrop') {
    await runAppleGrowAndDropFromConfig({
      sprite, animConfig, layout, variant, tuning,
      finalX, finalY, designW, designH,
      textureMap, nativeFacing, cancel,
    });
  } else {
    sprite.x = finalX;
    sprite.y = finalY;
  }
  if (cancel?.fastForwarded) restoreSpriteAtRest(sprite);
}

/**
 * Merge theme-level tuning with optional per-variant overrides.  A
 * `tuning` block inside the `standard` or `wide` layout shallow-
 * overrides the theme-level `tuning` -- any scalar (e.g.
 * `jumpArcHeightScale`) defined inside the variant wins.  Nested
 * objects (like `phases`) are replaced wholesale, so if you want
 * per-variant phase tuning, copy the whole `phases` block into the
 * variant's `tuning`.
 *
 * @param {{ tuning?: Object }} animConfig
 * @param {{ tuning?: Object }} layout
 */
export function mergeTuning(animConfig, layout) {
  return { ...(animConfig?.tuning || {}), ...(layout?.tuning || {}) };
}

function runFenceJumpFromConfig({ sprite, animConfig, layout, variant, tuning, finalX, finalY, designW, designH, jumpSoundSrc, textureMap, nativeFacing, cancel }) {
  const idx = Math.floor(Math.random() * layout.jumpStartPoints.length);
  const [jsxPct, jsyPct] = layout.jumpStartPoints[idx];
  const [jexPct, jeyPct] = layout.jumpEndPoints[idx];
  const jumpStartX = jsxPct * designW;
  const jumpStartY = jsyPct * designH;
  const jumpEndX = jexPct * designW;
  const jumpEndY = jeyPct * designH;
  const spriteW = sprite._spriteW ?? sprite.width;
  const startX = jumpStartX > designW / 2 ? designW + spriteW : -spriteW;
  const startY = jumpStartY;

  // Look up the walk and jump cycle keyframes for the current variant,
  // if the theme declares them.  Absence falls back to plain linear
  // motion with no frame animation.
  const walkCycleName = layout.walkCycle;
  const jumpCycleName = layout.jumpCycle;
  const walkKeyframes = walkCycleName ? (animConfig.cycles?.[walkCycleName]?.[variant] || []) : [];
  const jumpKeyframes = jumpCycleName ? (animConfig.cycles?.[jumpCycleName]?.[variant] || []) : [];

  // walkCycleWidth is stored as a fraction of design width so it scales
  // with variant.  Default of 0.07 = one trot cycle per 7% of scene width.
  const walkCycleWidth = (tuning.walkCycleWidth ?? 0.07) * designW;

  return runFenceJump(sprite, {
    startX, startY, jumpStartX, jumpStartY, jumpEndX, jumpEndY,
    finalX, finalY, velocity: layout.velocity,
    jumpSoundSrc,
    arcHeightScale: tuning.jumpArcHeightScale,
    airSpeedMultiplier: tuning.jumpAirSpeedMultiplier,
    nativeFacing,
    walkKeyframes, walkCycleWidth,
    jumpKeyframes,
    textureMap,
    soundLeadTimeMs: tuning.soundLeadTimeMs ?? 0,
  }, cancel);
}

function runCoopRampFromConfig({ sprite, animConfig, layout, variant, tuning, finalX, finalY, designW, designH, textureMap, nativeFacing, cancel }) {
  const zr = layout.startingZoneRect;
  const rectX = zr.x * designW;
  const rectY = zr.y * designH;
  const rectW = zr.width * designW;
  const rectH = zr.height * designH;
  const startX = rectX + Math.random() * rectW;
  const startY = rectY + Math.random() * rectH;

  const rampPoints = layout.rampPathPoints.map(([px, py]) => [px * designW, py * designH]);

  // Which named cycle (if any) to play per step in this phase.  Only
  // chicks use 'hop' in Phase 1c; other coopRamp themes fall back to
  // plain straight-line steps.
  const cycleName = layout.stepCycle;
  const cycleFrames = cycleName ? (animConfig.cycles?.[cycleName]?.[variant] || []) : [];

  // Reference distance for field-phase tilt magnitude scaling.  Given
  // as a fraction of design width in config (so it scales naturally
  // across variants); resolved to design pixels here.  A value of 0
  // or undefined disables scaling.
  const fieldTiltRefPct = tuning.fieldTiltReferenceDistance;
  const fieldTiltReferenceDist = (typeof fieldTiltRefPct === 'number' && fieldTiltRefPct > 0)
    ? fieldTiltRefPct * designW
    : Infinity;

  return runCoopRamp(sprite, {
    startX, startY, rampPoints, finalX, finalY,
    velocity: layout.velocity,
    rampVelocity: layout.rampVelocity,
    hopWidth: (layout.hopWidth ?? 0) * designW,
    withHop: !!layout.withHop,
    keyframes: cycleFrames,
    textureMap,
    nativeFacing,
    tuning,
    soundLeadTimeMs: tuning.soundLeadTimeMs ?? 0,
    fieldTiltReferenceDist,
  }, cancel);
}

/**
 * Translate the sideEntrance layout config into concrete runtime
 * coordinates and call runSideEntrance.  Picks the entrance side
 * based on finalX vs midPointList, then samples a random start point
 * along the line between startPoint1 and startPoint2 on that side.
 */
function runSideEntranceFromConfig({ sprite, animConfig, layout, variant, tuning, finalX, finalY, designW, designH, textureMap, nativeFacing, cancel, side }) {
  // Use the pre-computed side from computeSideEntranceOrder when
  // available; fall back to the midPointList heuristic otherwise.
  if (!side) {
    const midPointsPx = (layout.midPointList ?? []).map((p) => p * designW);
    side = pickSideForFinalX(finalX, midPointsPx);
  }

  // Random point along the line between startPoint1 and startPoint2
  // on the chosen side.
  const p1 = side === 'left' ? layout.leftSideStartPoint1  : layout.rightSideStartPoint1;
  const p2 = side === 'left' ? layout.leftSideStartPoint2  : layout.rightSideStartPoint2;
  const t = Math.random();
  const startX = lerp(p1[0] * designW, p2[0] * designW, t);
  const startY = lerp(p1[1] * designH, p2[1] * designH, t);

  const cycleName = layout.walkCycle;
  const keyframes = cycleName ? (animConfig.cycles?.[cycleName]?.[variant] || []) : [];

  return runSideEntrance(sprite, {
    startX, startY, finalX, finalY,
    velocity: layout.velocity,
    keyframes,
    textureMap,
    nativeFacing,
    soundLeadTimeMs: tuning.soundLeadTimeMs ?? 0,
  }, cancel);
}

// --- Flower-grow sequence (flowers) -------------------------------------

/**
 * Animate a single flower's entrance: a pure IN-PLACE cycle playback
 * with no linear base motion.  The grow cycle itself handles the
 * scale-up and deltaX/deltaY offsets that make the flower "pop out
 * of the ground."  Matches the legacy FlowerEntranceAnimationOperation
 * which simply invokes the `flowerGrow` sprite animation once per
 * object at its final position.
 *
 * @param {import('pixi.js').Container} sprite
 * @param {{
 *   finalX, finalY,
 *   keyframes, textureMap,
 *   nativeFacing, soundLeadTimeMs,
 * }} cfg
 * @param {{ cancelled: boolean }} [cancel]
 */
export async function runFlowerGrow(sprite, cfg, cancel) {
  const {
    finalX, finalY,
    keyframes = [], textureMap = {},
    nativeFacing = 'left',
    soundLeadTimeMs = 0,
  } = cfg;

  setSpriteLogicalPos(sprite, finalX, finalY);
  const cycleDur = cycleNaturalDuration(keyframes);
  const durationMs = cycleDur > 0 ? cycleDur * 1000 : 1000;

  // Single-step "motion" with x1==x2, y1==y2 -- the base position is
  // static at finalX/finalY; the cycle's deltaX/deltaY keyframes
  // provide the only motion, and its scaleX/scaleY keyframes grow
  // the flower.  The authored deltaY track in animations.json is
  // responsible for keeping the stem's visible bottom fixed while the
  // sprite scales up; tune those values in the keyframes directly.
  await runStepWithCycle({
    sprite,
    x1: finalX, y1: finalY, x2: finalX, y2: finalY,
    durationMs,
    keyframes, textureMap, nativeFacing,
    facingRight: null,   // flowers don't flip
    cycles: 1,
    soundLeadTimeMs, cancel,
  });
  if (shouldExitEntrance(sprite, cancel)) return;
  setSpriteLogicalPos(sprite, finalX, finalY);
}

function runFlowerGrowFromConfig({ sprite, animConfig, layout, variant, tuning, finalX, finalY, textureMap, nativeFacing, cancel }) {
  const cycleName = layout.stepCycle || 'grow';
  const keyframes = animConfig.cycles?.[cycleName]?.[variant] || [];
  return runFlowerGrow(sprite, {
    finalX, finalY,
    keyframes,
    textureMap,
    nativeFacing,
    soundLeadTimeMs: tuning.soundLeadTimeMs ?? 0,
  }, cancel);
}

// --- Apple grow-and-drop sequence (apples) ------------------------------

/**
 * Interpolate the Y position on a branch-path curve for a given X.
 * `branchPath` is a sorted array of [x, y] design-pixel pairs.  For
 * X values outside the path's range, returns the nearest endpoint's
 * Y (clamped).  Matches the legacy manager's linear interpolation
 * between consecutive branch points.
 *
 * @param {Array<[number, number]>} branchPath
 * @param {number} x
 * @returns {number}
 */
export function interpolateBranchY(branchPath, x) {
  if (!branchPath || branchPath.length === 0) return 0;
  if (x <= branchPath[0][0]) return branchPath[0][1];
  if (x >= branchPath[branchPath.length - 1][0]) return branchPath[branchPath.length - 1][1];
  for (let i = 1; i < branchPath.length; i++) {
    const [x1, y1] = branchPath[i - 1];
    const [x2, y2] = branchPath[i];
    if (x >= x1 && x <= x2) {
      const t = (x - x1) / Math.max(1e-6, x2 - x1);
      return y1 + (y2 - y1) * t;
    }
  }
  return branchPath[branchPath.length - 1][1];
}

/**
 * Apple entrance: a blossom flower appears on the branch, then an
 * apple grows out of it, drops to the ground, and bounces on impact.
 * Four phases, each driven by a separate keyframe cycle:
 *
 *   0. BLOSSOM at (finalX, branchY).  Plays `blossomKeyframes` once
 *      if supplied.  Flower texture swaps through the 6 bloom frames,
 *      scale 0.2 → 1.0, alpha 0.5 → 1.0 → 0 (fades out at end so
 *      the subsequent grow can transition cleanly).  Optional --
 *      omit blossomKeyframes to skip straight to grow.
 *   1. GROW at (finalX, branchY).  Plays `growKeyframes` once.  The
 *      cycle handles scale-up from 0.1 to 1.0, rotation from -90° to
 *      0°, alpha 0.5→1.0, and texture swaps through the 4 apple
 *      faces (plain → lids → half-eyes → eyes+eyebrows).
 *   2. DROP from (finalX, branchY) to (finalX, finalY), linear motion
 *      with `wobbleKeyframes` cycle looping.  Duration is velocity-
 *      weighted: dropDuration = (offset + slope * |dy|) / velocity.
 *   3. BOUNCE at (finalX, finalY).  Plays `bounceKeyframes` once --
 *      the squish/unsquish on ground impact.
 *
 * The blossom phase here is a SIMPLIFICATION of the legacy behavior
 * (where the flower animated on a separate sprite behind the apple
 * IN PARALLEL with the grow phase).  Playing them in sequence on
 * one sprite achieves a similar visual effect without the complexity
 * of a dual-sprite entrance, and matches the "blossom precedes
 * apple" interpretation of the scene.
 *
 * @param {import('pixi.js').Container} sprite
 * @param {{
 *   branchY, finalX, finalY,
 *   dropVelocity, dropVelocityOffset, dropVelocitySlope,
 *   blossomKeyframes, growKeyframes, wobbleKeyframes, bounceKeyframes,
 *   textureMap, nativeFacing, soundLeadTimeMs,
 * }} cfg
 * @param {{ cancelled: boolean }} [cancel]
 */
export async function runAppleGrowAndDrop(sprite, cfg, cancel) {
  const {
    branchY, finalX, finalY,
    dropVelocity, dropVelocityOffset = 0, dropVelocitySlope = 0.6,
    blossomKeyframes = [],
    growKeyframes = [], wobbleKeyframes = [], bounceKeyframes = [],
    textureMap = {}, nativeFacing = 'left',
    soundLeadTimeMs = 0,
    appleGrowStartAfterBlossomSeconds = 0.5,
  } = cfg;

  setSpriteLogicalPos(sprite, finalX, branchY);

  // Phase 0 + 1: BLOSSOM and GROW run in PARALLEL with a time offset.
  //
  // The blossom plays on the sprite's dedicated overlay sub-container
  // (sprite._blossomOverlay) so it can be visible at the same time as
  // the apple's grow animation on the main inner sprite.  The apple
  // grow starts `appleGrowStartAfterBlossomSeconds` after the blossom
  // begins -- matching the legacy "apple grows out of the blooming
  // flower" timing.  If the sprite has no overlay (theme doesn't
  // configure sprites.blossom), we fall back to sequential blossom
  // then apple on the same sprite.
  const overlay = sprite._blossomOverlay;
  const canParallelize = !!overlay && blossomKeyframes.length > 0;

  if (canParallelize) {
    // Position the overlay sub-container at the same local origin as
    // the main sprite (it's already a child of sprite so local 0,0
    // puts it at the main sprite's world position).
    overlay.x = 0;
    overlay.y = 0;

    // CRITICAL: hide the main apple sprite before the blossom starts.
    // Otherwise during the `appleGrowStartAfterBlossomSeconds` wait
    // the main sprite sits visibly at full size showing its static
    // apple texture (scene.js sets visible=true and alpha=1 by
    // default), which looks like a pre-grown apple briefly appearing
    // before the blossom even begins.  The grow cycle's first
    // keyframe sets alpha=0.5, so the apple becomes visible exactly
    // when the grow cycle kicks in.
    if (sprite._inner) sprite._inner.alpha = 0;

    const blossomDur = cycleNaturalDuration(blossomKeyframes) * 1000;
    const growDur    = growKeyframes.length > 0
      ? cycleNaturalDuration(growKeyframes) * 1000
      : 0;

    // Blossom animation (on the overlay sub-container).
    //
    // runStepWithCycle's cleanup at end-of-cycle forcibly resets
    // inner.alpha to 1.0 -- which UNDOES the blossom's intended
    // faded-out final state and makes the static flower texture
    // reappear behind the apple while the apple's grow cycle is
    // still running.  Chain a .then() to force the overlay back to
    // alpha=0 the instant the cycle promise resolves, so the blossom
    // stays invisible until the parallel apple-grow phase ends.
    const blossomPromise = runStepWithCycle({
      sprite: overlay,
      x1: 0, y1: 0, x2: 0, y2: 0,
      durationMs: blossomDur,
      keyframes: blossomKeyframes, textureMap, nativeFacing,
      facingRight: null,
      cycles: 1,
      soundLeadTimeMs, cancel,
    }).then(() => {
      if (overlay._inner) overlay._inner.alpha = 0;
    });

    // Apple grow animation (on the main inner sprite) -- starts after
    // the configured delay, in parallel with the ongoing blossom.
    const growPromise = (async () => {
      if (growKeyframes.length === 0) {
        // No grow cycle configured: restore main sprite visibility
        // so the apple at least shows up after the blossom.
        if (sprite._inner) sprite._inner.alpha = 1;
        return;
      }
      await waitForDelay(appleGrowStartAfterBlossomSeconds * 1000, cancel);
      if (cancel?.cancelled || cancel?.fastForwarded) return;
      await runStepWithCycle({
        sprite,
        x1: finalX, y1: branchY, x2: finalX, y2: branchY,
        durationMs: growDur,
        keyframes: growKeyframes, textureMap, nativeFacing,
        facingRight: null,
        cycles: 1,
        soundLeadTimeMs, cancel,
      });
    })();

    await Promise.all([blossomPromise, growPromise]);
    if (shouldExitEntrance(sprite, cancel)) return;

    // After parallel phase: ensure the overlay is fully hidden and
    // the main sprite is in its rested full-grown state.  The grow
    // cycle's cleanup in runStepWithCycle already resets alpha to 1
    // on the main inner sprite; set defensively here too in case
    // growKeyframes was empty.
    if (overlay._inner) overlay._inner.alpha = 0;
    if (sprite._inner) sprite._inner.alpha = 1;
  } else {
    // Fallback: sequential blossom (on main sprite) then grow.
    if (blossomKeyframes.length > 0) {
      const blossomDur = cycleNaturalDuration(blossomKeyframes) * 1000;
      await runStepWithCycle({
        sprite,
        x1: finalX, y1: branchY, x2: finalX, y2: branchY,
        durationMs: blossomDur,
        keyframes: blossomKeyframes, textureMap, nativeFacing,
        facingRight: null,
        cycles: 1,
        soundLeadTimeMs, cancel,
      });
      if (shouldExitEntrance(sprite, cancel)) return;
    }

    if (growKeyframes.length > 0) {
      const growDur = cycleNaturalDuration(growKeyframes) * 1000;
      await runStepWithCycle({
        sprite,
        x1: finalX, y1: branchY, x2: finalX, y2: branchY,
        durationMs: growDur,
        keyframes: growKeyframes, textureMap, nativeFacing,
        facingRight: null,
        cycles: 1,
        soundLeadTimeMs, cancel,
      });
      if (shouldExitEntrance(sprite, cancel)) return;
    }
  }

  // Phase 2: DROP from branch to final with wobble cycle
  const dropDist = Math.abs(finalY - branchY);
  if (dropDist > 1) {
    const dropSec = (dropVelocityOffset + dropVelocitySlope * dropDist) / Math.max(1, dropVelocity);
    const dropMs = dropSec * 1000;
    const wobbleNatMs = cycleNaturalDuration(wobbleKeyframes) * 1000;
    const wobbleCycles = wobbleNatMs > 0
      ? Math.max(1, Math.round(dropMs / wobbleNatMs))
      : 1;
    await runStepWithCycle({
      sprite,
      x1: finalX, y1: branchY, x2: finalX, y2: finalY,
      durationMs: dropMs,
      keyframes: wobbleKeyframes, textureMap, nativeFacing,
      facingRight: null,
      cycles: wobbleCycles,
      soundLeadTimeMs, cancel,
    });
    if (shouldExitEntrance(sprite, cancel)) return;
  }

  // Phase 3: BOUNCE on impact at final position
  if (bounceKeyframes.length > 0) {
    const bounceDur = cycleNaturalDuration(bounceKeyframes) * 1000;
    await runStepWithCycle({
      sprite,
      x1: finalX, y1: finalY, x2: finalX, y2: finalY,
      durationMs: bounceDur,
      keyframes: bounceKeyframes, textureMap, nativeFacing,
      facingRight: null,
      cycles: 1,
      soundLeadTimeMs, cancel,
    });
    if (shouldExitEntrance(sprite, cancel)) return;
  }

  setSpriteLogicalPos(sprite, finalX, finalY);
}

function runAppleGrowAndDropFromConfig({ sprite, animConfig, layout, variant, tuning, finalX, finalY, designW, designH, textureMap, nativeFacing, cancel }) {
  // Resolve branch path from normalized coords to design pixels, then
  // sort by X so interpolateBranchY's linear scan is monotonic.
  const branchPath = (layout.branchPathPoints || [])
    .map(([px, py]) => [px * designW, py * designH])
    .sort((a, b) => a[0] - b[0]);

  // Legacy positions each apple so its CENTER sits on the branch
  // curve at the apple's center-X.  In our coordinate system the
  // sprite container's (x, y) is the top-left, so:
  //   1. Look up branch Y at finalX + spriteW/2 (center X)
  //   2. Subtract spriteH/2 so the apple's visual center lands on
  //      the branch line (container top-left goes UP by half height)
  // Matches AppleGrowAndDropEntranceManager.m's
  //   stemHorizPlacement = inEndPoint.x + inStemPoint.x;
  //   return CGPointMake(inEndPoint.x, stemVertPlacement - inStemPoint.y);
  const spriteW = sprite._spriteW ?? 0;
  const spriteH = sprite._spriteH ?? 0;
  const stemX = finalX + spriteW / 2;
  const branchCenterY = interpolateBranchY(branchPath, stemX);
  const branchY = branchCenterY - spriteH / 2;

  const blossomKeyframes = animConfig.cycles?.blossom?.[variant] || [];
  const growKeyframes    = animConfig.cycles?.grow?.[variant]    || [];
  const wobbleKeyframes  = animConfig.cycles?.wobble?.[variant]  || [];
  const bounceKeyframes  = animConfig.cycles?.bounce?.[variant]  || [];

  return runAppleGrowAndDrop(sprite, {
    branchY, finalX, finalY,
    dropVelocity:       layout.dropVelocity       ?? layout.velocity,
    dropVelocityOffset: layout.dropVelocityOffset ?? 0,
    dropVelocitySlope:  layout.dropVelocitySlope  ?? 0.6,
    blossomKeyframes, growKeyframes, wobbleKeyframes, bounceKeyframes,
    textureMap, nativeFacing,
    soundLeadTimeMs: tuning.soundLeadTimeMs ?? 0,
  }, cancel);
}
